/** lag-richtext
 * Accepts text which may contain certain accepted markup which is then converted (safely) into HTML.
 * Only certain markup is accepted.
 *  
 *  EXAMPLE USAGE:
 *      <lag-richtext>your content here</lag-richtext>
 *  OR:
 *      lagRichText.text = 'your content here';
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';

import { defineElement } from '../../utility/defineElement.js';
import { log } from '../../app/log.js';

import { template } from './lag-richtext.html.js';

defineElement(class extends PolymerElement {

    static get is() { return 'lag-richtext'; }

    static get template() { return template; }

    set text(val) {
        this.innerText = val; // Use innerText to prevent XSS attacks (DONT USE innerHTML). Note this will trigger _slotChanged.
    }

    connectedCallback() {
        super.connectedCallback();

        // ...on content mutation.
        try {
            const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
            new MutationObserver(_ => { this._slotChanged(); }).observe(this, { characterData: true, childList: true, subtree: true });
        }
        catch (_ex) {
            // Some older browsers don't support MutationObserver,
            // instead we will use slotchange event, though this event only detects node removal/addition and
            // not content change (eg innerText/innerHTML).
            this.$.slot.addEventListener('slotchange', () => { this._slotChanged(); });
        }
    }

    ready() {
        super.ready();

        this._slotChanged();
    }

    _slotChanged() {
        // When we set innerHTML below it will cause a slotchanged/mutation event which in-turn will call this function again.
        // In order to avoid an infinite loop we have to flag when an update is occuring and abort the subsequent event.
        this._slotChanged_updating = !this._slotChanged_updating; // Toggle, if was updating now we're not so toggle false, if was not then we will be so toggle true.
        if (!this._slotChanged_updating) return; // If we just toggled updating to false then we are skipping a previous update, so dont continue.

        // Pull text out from 'slot'.
        // Use innerHTML to get encoded text rather than raw text. This is important to avoid pulling
        // in html tags, eg "&lt;script&gt;alert('attack');&lt;/script&gt;" should be encoded rather than
        // <script>alert('attack');</script> which is what would be pulled in if we used innerText or textContent.
        // The content *should* already be secure, or else we've already been compromised anyway.
        const text = this.innerHTML.trim();

        // Encode and apply the new text.
        // NOTE: This will trigger another slot changed, but the _slotChanged_updating call at the top should debounce.
        this.innerHTML = this._encodeText(text);
    }

    _encodeText(text) {
        text = this._stripHtmlTags(text);
        text = this._encodeSpecials(text);

        // "BBCode" encoding...
        [
            ['br', '<br>'],
            ['b', '<b>', '</b>'],
            ['i', '<i>', '</i>'],
            ['p', '<p>', '</p>'],
            ['c', '<span class="center">', '</span>'],
            ['center', '<span class="center">', '</span>'],
            ['u', '<span class="underline">', '</span>'],
            ['s', '<span class="strikethrough">', '</span>'],
            ['q', '<span class="quote">', '</span>'],
            ['quote', '<span class="quote">', '</span>'],
            ['big', '<span class="big">', '</span>'],
            ['small', '<span class="small">', '</span>'],
            ['sup', '<sup>', '</sup>'],
            ['sub', '<sub>', '</sub>']
        ].forEach(bb => {
            text = this._encodeBBCodes(text, bb[0], bb[1], bb[2]);
        });

        text = this._encodeUrls(text);

        return text;
    }
    _stripHtmlTags(text) {
        return text.replace(/<[^>]*>/i, '');
    }
    _encodeSpecials(text) {
        return text
            .replace(/&lt;\s*br\s*&gt;/i, '<br>')
            .replace('\n', '<br>')
            .replace(/&amp;([a-z0-9]{3,5});/i, '&$1;');
    }
    _encodeBBCodes(text, tag, openMarkup, closeMarkup) {
        if (!closeMarkup) {
            // Replace single tag, eg [tag] or [tag/]
            return text.replace(new RegExp(`\\[${tag}\\s*/?\\]`, 'i'), openMarkup);
        }
        else {
            // Replace paired tag, eg [tag]...[/tag]
            return text.replace(
                new RegExp(`\\[${tag}\\](.*?)\\[/\\s*${tag}\\]`, 'i'),
                openMarkup + '$1' + closeMarkup);
        }
    }
    _encodeUrls(text) {
        const rxAllowedChars = '[-_a-zA-Z0-9/:=&\\?\\s]+';

        // [url]link[/url]
        text = text.replace(
            new RegExp(`\\[url\\s*\\](${rxAllowedChars})\\[/\\s*url\\]`, 'i'),
            '<a rel="nofollow" target="_blank" href="$1">$1</a>');

        // [url=link]text[/url]
        text = text.replace(
            new RegExp(`\\[url=(${rxAllowedChars})\\s*\\](${rxAllowedChars})\\[/\\s*url\\]`, 'i'),
            '<a rel="nofollow" target="_blank" href="$1">$2</a>');

        return text;
    }
});